// Packages
import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

// Hooks
import useDataSourceManager from "hooks/useDataSourceManager";

// Utils
import localStorageUtil from "utils/localStorageUtil";

import { styled, useTheme } from "@mui/material/styles";
import {
    Box,
    Card,
    List,
    ListItem,
    Typography,
    Stack,
    CircularProgress
} from "@mui/material";

import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";

import { ReactComponent as SettingIcon } from "assets/images/icons/icn_table_cell_settings.svg";
import { ReactComponent as SettingHoverIcon } from "assets/images/icons/icn_table_cell_settings_hover.svg";

import getSwitchPortStatus from "utils/getSwitchPortStatus";

const TitleTypography = styled(Typography)(({ theme }) => ({
    margin: 0,
    color: theme.palette.primary.main,
    fontWeight: theme.typography.customFontWeight[2],
    fontSize: theme.typography.customFontSize[3],
    textAlign: "left",
}));

const ContentTypography = styled(Typography)(({ theme }) => ({
    margin: 0,
    color: theme.palette.primary.black,
    fontSize: theme.typography.customFontSize[2],
}));

const PortsContent = ({ auth, infoData }) => {
    const theme = useTheme();
    const { scopeData } = useOutletContext();
    const [hover, setHover] = useState(false);

    const [portUuid, _setPortUuid] = useState(uuidv4());
    const [portData, setPortData] = useState([]);
    const [portLoading, setPortLoading] = useState(false);
    const { dataMap, getData, sendRequest } = useDataSourceManager();

    const navigate = useNavigate();

    useEffect(() => {
        const standalone = !auth.orgs.id || Object.keys(auth.orgs).length === 0;
        const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/ports.read`;
        const restfulParamsConfig = {
            standalone: {
                orgId: auth.realm.id,
                siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
            },
            nonStandalone: {
                entId: auth.realm.id,
                orgId: auth.orgs.id,
                siteId: auth.scopeLevel === 'org' ? infoData.siteId : auth.sites.id,
            },
        };

        const requestParams = {
            "data": {
                "deviceIds": [infoData.macAddress]
            }
        };

        const mode = standalone ? "standalone" : "nonStandalone";
        const restfulParams = restfulParamsConfig[mode];
        setPortLoading(true);
        sendRequest(apiUrl, portUuid, restfulParams, requestParams);
    }, [])

    useEffect(() => {
        const response = getData(portUuid);
        if (response && response.data) {
            const data = response.data
            setPortData(data)
            setPortLoading(false);
        }
    }, [dataMap]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: 560 }}>
            <Box width="100%" padding="16px">
                <OutlineRectangleButton
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    startIcon={hover ? <SettingHoverIcon /> : <SettingIcon />}
                    width="100%"
                    height="32px"
                    onClick={() => {
                        if (auth.scopeLevel === "org") {
                            const selectedSite = scopeData.site.find(site => site.id === infoData.siteId);
                            if(selectedSite) localStorageUtil.updateItemField("selectBreadcrumbs", "site", selectedSite);
                        }

                        navigate("/site/settings", {
                            state: {
                                navigateTab: "wired-port-management",
                                navigateModelName: infoData.modelName,
                                navigateMacAddress: infoData.macAddress
                            }
                        });
                    }}
                >
                    Port Manager
                </OutlineRectangleButton>
            </Box>

            <Box width="100%" padding="16px">
                <Card sx={{ padding: 2 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        sx={{
                            px: 1.5,
                            pb: 1,
                            borderBottom: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
                            color: theme.palette.primary.main,
                            marginBottom: 2
                        }}
                    >
                        {["Port", "Status", "Type", "PVID", "Allowed VLANs"].map((item, index) => (
                            <TitleTypography
                                key={index}
                                sx={{ flex: index === 4 ? 2 : 1, }}
                            >
                                {item}
                            </TitleTypography>
                        ))}
                    </Stack>
                    <List disablePadding>
                        {portLoading ?
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 2,
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    margin: 2,
                                }}>
                                <CircularProgress />
                            </Box> :
                            portData.map((dataItem) => {
                                const switchPort = dataItem.switchPorts;
                                const wiredPort = dataItem["wired.port"];

                                return wiredPort.map((wiredItem, index) => (
                                    <ListItem
                                        key={`wired-${index}-${index}`}
                                        divider
                                        sx={{
                                            py: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            borderBottom: "none",
                                        }}
                                    >
                                        <ContentTypography sx={{ flex: 1 }}>{wiredItem.portIndex}</ContentTypography>
                                        <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                                            {switchPort.length === 0
                                                ? "-"
                                                : getSwitchPortStatus(switchPort.find(portItem => portItem.portStaIndex === wiredItem.portIndex))
                                            }
                                        </Box>
                                        <ContentTypography sx={{ flex: 1 }}>{wiredItem.portType}</ContentTypography>
                                        <ContentTypography sx={{ flex: 1 }}>{wiredItem.PVID}</ContentTypography>
                                        <ContentTypography sx={{ flex: 2 }}>{wiredItem.portType === "access" ? "-" : wiredItem.allowVlan || "-"}</ContentTypography>
                                    </ListItem>
                                ));
                            })}
                    </List>
                </Card>
            </Box>
        </Box>
    );
};

export default PortsContent;
